/**
 * Analytics loader for GA4, Clarity, and Naver Web Analytics
 * - Dynamically loads scripts only on production domain (solcombine.com)
 * - Excludes prerender (Playwright), internal traffic, and localhost
 */

const ALLOWED_HOSTS = ['solcombine.com', 'www.solcombine.com'];

let isInitialized = false;
let isEnabled = false;

/**
 * Check if analytics should be enabled
 */
function shouldEnableAnalytics() {
  try {
    // Check hostname
    if (!ALLOWED_HOSTS.includes(window.location.hostname)) {
      return false;
    }
    // Check for Playwright prerender (webdriver)
    if (navigator.webdriver === true) {
      return false;
    }
    // Check for internal traffic flag
    if (localStorage.getItem('sc_internal') === '1') {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Handle ?internal=1 or ?internal=0 in URL
 */
function handleInternalFlag() {
  try {
    const params = new URLSearchParams(window.location.search);
    const internalParam = params.get('internal');
    if (internalParam === '1') {
      localStorage.setItem('sc_internal', '1');
    } else if (internalParam === '0') {
      localStorage.removeItem('sc_internal');
    }
  } catch {
    // ignore
  }
}

/**
 * Load GA4 gtag.js dynamically
 */
function loadGA4(measurementId) {
  try {
    if (!measurementId) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());

    // Check for debug_mode in URL
    const params = new URLSearchParams(window.location.search);
    const debugMode = params.get('debug_mode') === '1';

    window.gtag('config', measurementId, {
      send_page_view: false, // SPA: send manually
      ...(debugMode && { debug_mode: true }),
    });
  } catch (e) {
    console.warn('[Analytics] GA4 load error:', e);
  }
}

/**
 * Load Microsoft Clarity dynamically
 */
function loadClarity(projectId) {
  try {
    if (!projectId) return;

    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", projectId);
  } catch (e) {
    console.warn('[Analytics] Clarity load error:', e);
  }
}

/**
 * Load Naver Web Analytics (wcslog.js + wcs_do)
 */
function loadNaverWA(accountId) {
  try {
    if (!accountId) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://wcs.naver.net/wcslog.js';
    script.onload = function() {
      if (typeof window.wcs_add === 'undefined') window.wcs_add = {};
      window.wcs_add.wa = accountId;
      if (typeof window.wcs_do === 'function') window.wcs_do();
    };
    document.head.appendChild(script);
  } catch (e) {
    console.warn('[Analytics] Naver WA load error:', e);
  }
}

/**
 * Initialize analytics (call once on app mount)
 */
export function initAnalytics() {
  try {
    if (isInitialized) return;
    isInitialized = true;

    handleInternalFlag();
    isEnabled = shouldEnableAnalytics();

    if (!isEnabled) {
      return;
    }

    // Load analytics tools based on env vars
    const ga4Id = process.env.REACT_APP_GA4_ID;
    const clarityId = process.env.REACT_APP_CLARITY_ID;
    const naverWaId = process.env.REACT_APP_NAVER_WA_ID;

    if (ga4Id) loadGA4(ga4Id);
    if (clarityId) loadClarity(clarityId);
    if (naverWaId) loadNaverWA(naverWaId);

    // Setup delegated click listeners for tel: and outbound links
    setupClickListeners();

    // Setup scroll depth tracking
    setupScrollDepth();
  } catch (e) {
    console.warn('[Analytics] init error:', e);
  }
}

/**
 * Track page view (call on route change)
 * @param {string} path - Current path
 * @param {string} title - Page title
 */
export function trackPageView(path, title) {
  try {
    if (!isEnabled) return;

    // GA4
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title,
        page_location: window.location.href,
      });
    }

    // Naver WA
    if (typeof window.wcs_do === 'function') {
      window.wcs_do();
    }

    // Reset scroll depth tracking for new page
    resetScrollDepth();
  } catch (e) {
    console.warn('[Analytics] trackPageView error:', e);
  }
}

/**
 * Track custom event
 * @param {string} name - Event name
 * @param {object} params - Event parameters
 */
export function trackEvent(name, params = {}) {
  try {
    if (!isEnabled) return;

    // GA4
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
  } catch (e) {
    console.warn('[Analytics] trackEvent error:', e);
  }
}

// CTA button patterns (class names or text content patterns)
const CTA_PATTERNS = [
  'consult', 'contact', 'cta', '상담', '문의', '도입', '신청', '시작'
];

/**
 * Check if element is a CTA button
 */
function isCTAButton(element) {
  try {
    if (!element) return false;
    const tagName = element.tagName?.toLowerCase();
    if (tagName !== 'button' && tagName !== 'a') return false;

    const className = (element.className || '').toLowerCase();
    const textContent = (element.textContent || '').trim();

    // Check class names
    for (const pattern of CTA_PATTERNS) {
      if (className.includes(pattern)) return true;
    }

    // Check text content for Korean CTA words
    for (const pattern of CTA_PATTERNS) {
      if (textContent.includes(pattern)) return true;
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Setup delegated click listeners for tel: links, outbound links, and CTA buttons
 */
function setupClickListeners() {
  try {
    document.addEventListener('click', (event) => {
      try {
        // Check for link clicks
        const linkTarget = event.target.closest('a[href]');
        if (linkTarget) {
          const href = linkTarget.getAttribute('href');
          if (!href) return;

          // tel: links
          if (href.startsWith('tel:')) {
            const phoneNumber = href.replace('tel:', '');
            trackEvent('tel_click', {
              phone_number: phoneNumber,
              source_page: window.location.pathname,
            });
            return;
          }

          // Outbound links (different domain)
          try {
            const url = new URL(href, window.location.origin);
            if (url.hostname !== window.location.hostname) {
              trackEvent('outbound_click', {
                link_domain: url.hostname,
                link_url: href,
                source_page: window.location.pathname,
              });
            }
          } catch {
            // Invalid URL, ignore
          }
        }

        // Check for CTA button clicks
        const buttonTarget = event.target.closest('button, a');
        if (buttonTarget && isCTAButton(buttonTarget)) {
          const ctaLabel = (buttonTarget.textContent || '').trim().slice(0, 50);
          trackEvent('cta_click', {
            cta_label: ctaLabel,
            source_page: window.location.pathname,
          });
        }
      } catch {
        // Ignore click handler errors
      }
    }, { passive: true });
  } catch (e) {
    console.warn('[Analytics] setupClickListeners error:', e);
  }
}

// Scroll depth state
let scrollDepthTriggered = { 25: false, 50: false, 75: false, 90: false };
let scrollDepthPage = '';

/**
 * Reset scroll depth tracking for new page
 */
function resetScrollDepth() {
  scrollDepthTriggered = { 25: false, 50: false, 75: false, 90: false };
  scrollDepthPage = window.location.pathname;
}

/**
 * Setup scroll depth tracking
 */
function setupScrollDepth() {
  try {
    let ticking = false;

    const checkScrollDepth = () => {
      try {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;

        if (docHeight <= 0) return;

        const scrollPercent = Math.round((scrollTop / docHeight) * 100);
        const thresholds = [25, 50, 75, 90];

        for (const threshold of thresholds) {
          if (scrollPercent >= threshold && !scrollDepthTriggered[threshold]) {
            scrollDepthTriggered[threshold] = true;
            trackEvent('scroll_depth', {
              percent: threshold,
              page_path: scrollDepthPage || window.location.pathname,
            });
          }
        }
      } catch {
        // Ignore scroll check errors
      }
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkScrollDepth();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  } catch (e) {
    console.warn('[Analytics] setupScrollDepth error:', e);
  }
}
